import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import api from '../services/api';
import HtmlExportService from '../services/HtmlExportService';
import ModalComponent from '../components/ModalComponent';
import { PagesContext } from './PagesContext';

interface PublishContextProps {
  publishPage: (
    apiPath: string,
    pageId: string,
    isRepublishing: boolean,
    setPageId: (value: string) => void,
  ) => Promise<void>,
  unpublishPage: (
    pageId: string,
    setPagePublishId: (value: string) => void,
  ) => Promise<void>,
  isInCooldown: boolean,
  setIsInCooldown: (value: boolean) => void,
  message: string,
}

export const PublishContext = createContext<PublishContextProps>({} as PublishContextProps);
PublishContext.displayName = 'PublishContext';

interface ProviderProps {
  children: JSX.Element
}

export const PublishProvider = ({ children }: ProviderProps) => {
  const { checkForStatus } = useContext(PagesContext);

  const [isInCooldown, setIsInCooldown] = useState(false);

  const [process, setProcess] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [operationModalOpen, setOperationModalOpen] = useState(false);
  const [message, setMessage] = useState('');

  const clearState = useCallback(() => {
    setIsInCooldown(false);
    setMessage('');
  }, []);

  function preparePageData(pageId: string) {
    const formData = new FormData();
    formData.append('file', HtmlExportService(), 'export.html');
    formData.append('page_id', pageId);

    return formData;
  }

  const unpublishPage = useCallback(async (
    pageId: string,
    setPagePublishId: (value: string) => void,
  ) => {
    let scopedMessage = '';
    if (pageId == null) return;

    clearState();
    setProcess('Unpublish');
    setModalTitle('Page unpublishing');

    await api.post(`/v1/web-process/${pageId}/unpublish`)
      .then(() => {
        setOperationModalOpen(true);
        setPagePublishId(null);
        checkForStatus(pageId);
      }).catch((err) => {
        scopedMessage = err.response.data.message;
      });

    if (scopedMessage === '') {
      setPagePublishId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkForStatus, clearState]);

  const publishPage = useCallback(async (
    apiPath: string,
    pageId: string,
    isRepublishing: boolean,
    setPageId: (value: string) => void,
  ) => {
    if (pageId == null) {
      return;
    }

    clearState();
    setModalTitle('Page publishing');
    setProcess(isRepublishing ? 'Republish' : 'Publish');

    const formData = preparePageData(pageId);

    await api.post(apiPath, formData, {
      headers: { 'content-type': 'multipart/form-data' },
    }).then(() => {
      setOperationModalOpen(true);
      setPageId(null);
      checkForStatus(pageId);
    }).catch((e) => {
      const { data, status } = e.response;

      if (status === 400) {
        setIsInCooldown(true);
        setMessage(data);
      }

      if (status === 422) {
        setIsInCooldown(true);
        setMessage(data?.error);
      }
    });
  }, [checkForStatus, clearState]);

  const exportValues = useMemo(() => ({
    publishPage,
    unpublishPage,
    isInCooldown,
    setIsInCooldown,
    message,
  }), [isInCooldown, message, publishPage, unpublishPage]);

  useEffect(() => {
    setIsInCooldown(isInCooldown);
  }, [isInCooldown]);

  return (
    <PublishContext.Provider value={exportValues}>
      {children}

      <ModalComponent
        show={operationModalOpen}
        closeModal={() => setOperationModalOpen(false)}
        onCloseModal={() => { setOperationModalOpen(false); }}
        title={modalTitle}
        hideConfirmButton
        hideHeaderCloseButton
        cancelButtonColor="outline-secondary"
        cancelButtonText="Close"
        backdrop="static"
        keyboard={false}
        centered
        footerClass="d-flex justify-content-center"
        bodyStyle={{
          maxHeight: '50vh',
          overflowY: 'auto',
        }}
      >
        {message && (
          <div className="alert alert-danger" role="alert">
            {message}
          </div>
        )}

        <div className="d-flex flex-column px-3">
          {process !== 'Unpublish' && (
            <>
              <p className="mb-2">
                <i className="fa-solid fa-check fa-fw me-2" />
                01 - Generating ZIP file
              </p>
              <p className="mb-2">
                <i className="fa-solid fa-check fa-fw me-2" />
                02 - Uploading
              </p>
              <p className="mb-2">
                <i className="fa-solid fa-check fa-fw me-2" />
                03 - Uploading published files
              </p>
            </>
          )}

          <div className="my-3">
            <p className="mb-3 text-center text-bold">
              {process}
              {' in progress.'}
              <br />
              Please wait until completion.
            </p>
            <p className="mb-2 fs-xs">*Note: Changes will not be reflected on the site until publishing is complete.</p>
          </div>
        </div>
      </ModalComponent>
    </PublishContext.Provider>
  );
};
